import { useCallback, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import agentService, {
    AgentAttachment,
    AgentConversationScope,
    AgentMessage,
    AgentPageContextPayload,
    AgentStreamEvent,
} from '@/services/agent/service'
import { AssistantSegment } from '../types'
import { invalidateForAgentMutation } from '../utils/mutationSync'

type TitleUpdateCallback = (title: string) => void

export const useAgentStream = () => {
    const queryClient = useQueryClient()
    const abortRef = useRef<AbortController | null>(null)
    const titleUpdateCallbackRef = useRef<TitleUpdateCallback | null>(null)
    const [isStreaming, setIsStreaming] = useState(false)
    const [isAwaitingFirstEvent, setIsAwaitingFirstEvent] = useState(false)
    const [segments, setSegments] = useState<AssistantSegment[]>([])
    const [error, setError] = useState<Error | null>(null)
    const [lastConversationId, setLastConversationId] = useState<string | null>(null)
    const [lastToolResult, setLastToolResult] = useState<{
        id: string
        name: string
        success: boolean
        result: unknown
        durationMs: number
    } | null>(null)

    const resetStreamState = useCallback(() => {
        setSegments([])
        setError(null)
        setLastToolResult(null)
    }, [])

    const clearSegments = useCallback(() => {
        setSegments([])
    }, [])

    const clearLastConversationId = useCallback(() => {
        setLastConversationId(null)
    }, [])

    const stop = useCallback(() => {
        abortRef.current?.abort()
        abortRef.current = null
        setIsStreaming(false)
        setIsAwaitingFirstEvent(false)
    }, [])

    const appendTextDelta = (delta: string) => {
        setSegments(prev => {
            const last = prev[prev.length - 1]
            if (last && last.kind === 'text') {
                return [...prev.slice(0, -1), { kind: 'text', text: last.text + delta }]
            }
            return [...prev, { kind: 'text', text: delta }]
        })
    }

    const upsertToolCall = (id: string, name: string, args?: Record<string, unknown>) => {
        setSegments(prev => {
            const existingIdx = prev.findIndex(s => s.kind === 'tool' && s.id === id)
            if (existingIdx >= 0) return prev
            return [...prev, { kind: 'tool', id, name, status: 'running', args }]
        })
    }

    const completeToolCall = (
        id: string,
        name: string,
        success: boolean,
        result: unknown,
        durationMs: number
    ) => {
        setSegments(prev => {
            const existingIdx = prev.findIndex(s => s.kind === 'tool' && s.id === id)
            if (existingIdx === -1) {
                return [...prev, { kind: 'tool', id, name, status: 'done', success, result, durationMs }]
            }
            const updated = [...prev]
            updated[existingIdx] = {
                ...(updated[existingIdx] as any),
                status: 'done',
                success,
                result,
                durationMs,
            }
            return updated
        })
    }

    const sendMessage = useCallback(
        async (
            message: string,
            conversationId?: string | null,
            history: AgentMessage[] = [],
            options?: {
                pageContext?: AgentPageContextPayload
                attachments?: AgentAttachment[]
                conversationScope?: AgentConversationScope
                studioDraftDocId?: string
            }
        ) => {
            abortRef.current?.abort()
            const controller = new AbortController()
            abortRef.current = controller

            resetStreamState()
            setIsStreaming(true)
            setIsAwaitingFirstEvent(true)

            try {
                await agentService.streamChat(
                    {
                        message,
                        conversationId: conversationId || undefined,
                        conversationScope: options?.conversationScope,
                        studioDraftDocId: options?.studioDraftDocId,
                        history,
                        pageContext: options?.pageContext,
                        attachments: options?.attachments || [],
                    },
                    (event: AgentStreamEvent) => {
                        if (event.type === 'token') {
                            setIsAwaitingFirstEvent(false)
                            appendTextDelta(event.content)
                        }

                        if (event.type === 'tool_call') {
                            setIsAwaitingFirstEvent(false)
                            upsertToolCall(event.id, event.name, event.args)
                        }

                        if (event.type === 'tool_result') {
                            completeToolCall(
                                event.id,
                                event.name,
                                event.success,
                                event.result,
                                event.durationMs
                            )
                            setLastToolResult({
                                id: event.id,
                                name: event.name,
                                success: event.success,
                                result: event.result,
                                durationMs: event.durationMs,
                            })
                            if (event.success) {
                                invalidateForAgentMutation(queryClient, event.name)
                            }
                        }

                        if (event.type === 'done') {
                            setLastConversationId(event.conversationId || null)
                            setIsStreaming(false)
                            setIsAwaitingFirstEvent(false)
                        }

                        if (event.type === 'title_updated') {
                            titleUpdateCallbackRef.current?.(event.title)
                        }

                        if (event.type === 'error') {
                            setError(new Error(event.message))
                            setIsStreaming(false)
                            setIsAwaitingFirstEvent(false)
                        }
                    },
                    controller.signal
                )
            } catch (err: any) {
                if (err.name !== 'AbortError') {
                    setError(err)
                }
            } finally {
                setIsStreaming(false)
                setIsAwaitingFirstEvent(false)
                abortRef.current = null
            }
        },
        [resetStreamState, queryClient]
    )

    const setOnTitleUpdate = useCallback((callback: TitleUpdateCallback | null) => {
        titleUpdateCallbackRef.current = callback
    }, [])

    return useMemo(
        () => ({
            sendMessage,
            isStreaming,
            isAwaitingFirstEvent,
            segments,
            stop,
            error,
            resetStreamState,
            clearSegments,
            clearLastConversationId,
            lastConversationId,
            setOnTitleUpdate,
            lastToolResult,
        }),
        [
            sendMessage,
            isStreaming,
            isAwaitingFirstEvent,
            segments,
            stop,
            error,
            resetStreamState,
            clearSegments,
            clearLastConversationId,
            lastConversationId,
            setOnTitleUpdate,
            lastToolResult,
        ]
    )
}
